import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
} from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import {
  getDisabilitiesOptions,
  getGenderOptions,
  getSchoolLevelOptions,
} from '../../constants/student';
import { STUDENT_MAXIMUM_AGE, STUDENT_MINIMUM_AGE } from '@duke-hq/shared-constants/students';
import { StudentEducationLevel, StudentGenderKind } from '@duke-hq/svc-api-db';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Select } from 'chakra-react-select';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { countries } from 'countries-list';
import { usiIdentityChanged } from '@duke-hq/lib/usi';
import { UsiVerificationResetWarning } from './UsiVerificationResetWarning';

type CountryOption = {
  label: string;
  value: string;
};

type AdditionalDetailsUpdateFormProps = {
  defaultDateOfBirth?: Date | string;
  defaultGender?: StudentGenderKind;
  defaultHighestEducation?: StudentEducationLevel;
  defaultDisabilities?: string[];
  defaultCountryOfBirth?: string;
  isUsiVerified?: boolean;
  updateAdditionalInfo: (values: AdditionalDetailsUpdateFormDataTypes) => void;
  onCancel: () => void;
  isDataSubmitting: boolean;
};

type AdditionalDetailsUpdateFormDataTypes = {
  dateOfBirth?: Date;
  gender?: StudentGenderKind;
  highestEducation?: StudentEducationLevel;
  disabilities: string[];
  haveDisability: boolean;
  countryOfBirth?: string;
};

export function AdditionalDetailsUpdateForm({
  defaultDateOfBirth,
  defaultGender,
  defaultHighestEducation,
  defaultDisabilities,
  defaultCountryOfBirth,
  isUsiVerified = false,
  updateAdditionalInfo,
  onCancel,
  isDataSubmitting,
}: AdditionalDetailsUpdateFormProps): React.ReactElement {
  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
  } = useForm<AdditionalDetailsUpdateFormDataTypes>();

  const [usiWarningShown, setUsiWarningShown] = useState(false);

  const today = djs();

  const genderOptions = useMemo(() => getGenderOptions(), []);
  const schoolLevelOptions = useMemo(() => getSchoolLevelOptions(), []);
  const disabilitiesOptions = useMemo(() => getDisabilitiesOptions(), []);

  // Calculate the upper and lower limits for the date of birth based on the minimum age
  const maxDate = today.subtract(STUDENT_MINIMUM_AGE, 'year').format('YYYY-MM-DD');
  const minDate = today.subtract(STUDENT_MAXIMUM_AGE, 'year').format('YYYY-MM-DD');

  const countriesOptions: CountryOption[] = useMemo(() => {
    return Object.entries(countries)
      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
      .map(([key, value]) => ({
        value: key,
        label: value.name,
      }));
  }, []);

  const willClearUsiVerification = (dateOfBirth: Date | string | undefined) =>
    isUsiVerified &&
    usiIdentityChanged(
      { dateOfBirth: defaultDateOfBirth ?? null },
      { dateOfBirth: dateOfBirth || undefined },
    );

  const usiWarningActive = usiWarningShown && willClearUsiVerification(watch('dateOfBirth'));

  const dateOfBirthWarnStyle = usiWarningActive
    ? { borderColor: 'orange.400', boxShadow: '0 0 0 1px var(--chakra-colors-orange-400)' }
    : {};

  function onSubmit(values: AdditionalDetailsUpdateFormDataTypes) {
    values.haveDisability = values.disabilities.length > 0;
    values.dateOfBirth = values.dateOfBirth ? new Date(values.dateOfBirth) : undefined;

    const clearsUsiVerification = willClearUsiVerification(values.dateOfBirth);

    if (clearsUsiVerification && !usiWarningShown) {
      setUsiWarningShown(true);
      return;
    }
    if (!clearsUsiVerification) {
      setUsiWarningShown(false);
    }

    updateAdditionalInfo(values);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', height: '100%' }} noValidate>
      <FormMainContainer>
        <FormContentContainer>
          <Heading fontSize="lg" size="xs" w="full">
            <Trans>Update Student Additional Information</Trans>
          </Heading>

          {usiWarningActive && <UsiVerificationResetWarning />}

          <HStack w="full" align="start">
            <FormControl isInvalid={!!errors.dateOfBirth}>
              <FormLabel htmlFor="dateOfBirth">
                <Trans>Date of birth</Trans>
              </FormLabel>
              <Input
                id="dateOfBirth"
                min={minDate}
                max={maxDate}
                {...register('dateOfBirth', {
                  min: {
                    value: minDate,
                    message: t`Please enter a valid date of birth. Age must be ${STUDENT_MINIMUM_AGE}-${STUDENT_MAXIMUM_AGE} years.`,
                  },
                  max: {
                    value: maxDate,
                    message: t`Please enter a valid date of birth. Age must be ${STUDENT_MINIMUM_AGE}-${STUDENT_MAXIMUM_AGE} years.`,
                  },
                  validate: (value) => {
                    if (!value) {
                      return true;
                    }
                    return djs(value).isValid() || t`Please enter a valid date`;
                  },
                })}
                type="date"
                defaultValue={
                  defaultDateOfBirth ? djs(defaultDateOfBirth).format('YYYY-MM-DD') : undefined
                }
                {...dateOfBirthWarnStyle}
              />
              <FormErrorMessage>{errors.dateOfBirth?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.gender}>
              <FormLabel>
                <Trans>Gender</Trans>
              </FormLabel>
              <Controller
                name="gender"
                control={control}
                defaultValue={defaultGender}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t`Select option`}
                    options={genderOptions}
                    value={genderOptions.find((option) => option.value === field.value)}
                    onChange={(val) => field.onChange(val?.value)}
                  />
                )}
              />
              <FormErrorMessage>{errors.gender?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors.highestEducation}>
            <FormLabel>
              <Trans>Highest completed school level</Trans>
            </FormLabel>
            <Controller
              name="highestEducation"
              control={control}
              defaultValue={defaultHighestEducation}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t`Select option`}
                  options={schoolLevelOptions}
                  value={schoolLevelOptions.find((option) => option.value === field.value)}
                  onChange={(val) => field.onChange(val?.value)}
                  maxMenuHeight={180}
                  selectedOptionColorScheme="brand"
                />
              )}
            />
            <FormErrorMessage>{errors.highestEducation?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.disabilities}>
            <FormLabel>
              <Trans>Disability and special assistance</Trans>
            </FormLabel>
            <Controller
              name="disabilities"
              control={control}
              defaultValue={[...(defaultDisabilities || [])]}
              render={({ field }) => (
                <Select
                  {...field}
                  isMulti
                  placeholder={t`Please select`}
                  options={disabilitiesOptions}
                  value={disabilitiesOptions.filter((option) =>
                    field.value?.includes(option.value),
                  )}
                  onChange={(val) => field.onChange(val.map((item) => item.value))}
                  maxMenuHeight={180}
                  selectedOptionColorScheme="brand"
                />
              )}
            />
            <FormErrorMessage>{errors.disabilities?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.countryOfBirth}>
            <FormLabel htmlFor="countryOfBirth">
              <Trans>Country of birth</Trans>
            </FormLabel>
            <Controller
              name="countryOfBirth"
              control={control}
              defaultValue={defaultCountryOfBirth}
              render={({ field }) => (
                <Select
                  placeholder={t`Please select`}
                  options={countriesOptions}
                  value={countriesOptions.find((option) => option.value === field.value)}
                  onChange={(val: CountryOption) => {
                    field.onChange(val?.value);
                  }}
                />
              )}
            />
            <FormErrorMessage>{errors.countryOfBirth?.message}</FormErrorMessage>
          </FormControl>
        </FormContentContainer>

        <FormFooterContainer>
          <ButtonGroup>
            <Button variant="secondary" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              isLoading={isDataSubmitting}
              type="submit"
              colorScheme={usiWarningActive ? 'orange' : undefined}
            >
              {usiWarningActive ? (
                <Trans>Save and clear USI verification</Trans>
              ) : (
                <Trans>Update</Trans>
              )}
            </Button>
          </ButtonGroup>
        </FormFooterContainer>
      </FormMainContainer>
    </form>
  );
}
